import { NavLink, useLocation } from 'react-router';
import {
  Bell,
  BookOpen,
  HelpCircle,
  Home,
  Link2,
  LogOut,
  Settings,
  Ticket,
  Users,
} from 'lucide-react';
import { useUnreadCount } from '@/components/features/notifications';
import { ROUTES } from '@/constants';
import { useLogout } from '@/services/queries';
import { useAppSelector } from '@/store/hooks';
import type { CurrentUser } from '@/types';
import { AppShell, navItemClass, type NavItem } from '@/shared/ui/app-shell';
import { Logo } from '@/shared/ui/logo';

/**
 * The customer portal's shell.
 *
 * All the layout lives in the shared AppShell; this supplies what is specific
 * to this portal - which items, which icons, who is signed in.
 *
 * Icons are 16px at stroke 1.8 to match the design. lucide's defaults are 24
 * and 2, which reads noticeably heavier beside 12.5px labels.
 */
const NAV: NavItem[] = [
  { to: ROUTES.dashboard, label: 'Dashboard', icon: <Home size={16} strokeWidth={1.8} /> },
  {
    to: ROUTES.reviewLinks,
    label: 'Review Links',
    icon: <Link2 size={16} strokeWidth={1.8} />,
  },
  {
    to: ROUTES.notifications,
    label: 'Notifications',
    icon: <Bell size={16} strokeWidth={1.8} />,
  },
  { to: ROUTES.profile, label: 'Profile', icon: <Users size={16} strokeWidth={1.8} /> },
  { to: ROUTES.issues, label: 'Issues', icon: <Ticket size={16} strokeWidth={1.8} /> },
  { to: ROUTES.manual, label: 'User Manual', icon: <BookOpen size={16} strokeWidth={1.8} /> },
  { to: ROUTES.faq, label: 'FAQ', icon: <HelpCircle size={16} strokeWidth={1.8} /> },
  { to: ROUTES.security, label: 'Settings', icon: <Settings size={16} strokeWidth={1.8} /> },
];

/**
 * Screen titles for the page header, from the design's own `meta` map.
 *
 * Kept beside the nav because they change together: a screen renamed in one
 * and not the other is how a header and a sidebar start disagreeing.
 */
const TITLES: Record<string, string> = {
  [ROUTES.dashboard]: 'Dashboard',
  [ROUTES.reviewLinks]: 'Review Links',
  [ROUTES.notifications]: 'Notifications',
  [ROUTES.profile]: 'Profile',
  [ROUTES.issues]: 'Support Issues',
  [ROUTES.manual]: 'User Manual',
  [ROUTES.faq]: 'FAQ',
  [ROUTES.security]: 'Settings',
};

/** portal_users.role, as the badge under the user's name reads it. */
const ROLE_LABELS: Record<CurrentUser['role'], string> = {
  owner: 'Owner',
  technician: 'Technician',
  viewer: 'Viewer',
};

export function Sidebar({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const user = useAppSelector((state) => state.auth.user);
  const logout = useLogout();
  const unread = useUnreadCount();
  // The unread count rides on the Notifications item - the shell's own badge,
  // so it collapses and themes with every other nav row.
  const nav = NAV.map((item) =>
    item.to === ROUTES.notifications ? { ...item, badge: unread.data } : item,
  );
  return (
    <AppShell
      title={TITLES[pathname] ?? ''}
      storageKey="nudent.sidebar.user"
      logo={<Logo />}
      nav={nav}
      user={
        user
          ? {
              name: user.name,
              org: user.organizationId ? 'Your lab' : undefined,
              role: ROLE_LABELS[user.role],
            }
          : undefined
      }
      renderLink={(item, content, collapsed) => (
        <NavLink
          to={item.to}
          title={collapsed ? item.label : undefined}
          className={({ isActive }) => navItemClass(isActive, collapsed)}
        >
          {content}
        </NavLink>
      )}
      footer={
        <>
          <button
            type="button"
            disabled={logout.isPending}
            onClick={() => {
              logout.mutate();
            }}
            className={navItemClass(false, false)}
          >
            <span className="flex size-4 shrink-0 items-center justify-center">
              <LogOut size={16} strokeWidth={1.8} />
            </span>
            <span className="truncate">Sign out</span>
          </button>
        </>
      }
    >
      {children}
    </AppShell>
  );
}
